import { Decimal } from 'decimal.js';
import {
  IOgqlColumn,
  IOgqlJobResult,
  IOgqlQueryResult,
  IOgqlV1JobResult,
  IOgqlV1QueryResult
} from '@opengov/ogql-types';
import {
  createReportConfigurationPresentation,
  EReportTargetComparisonOperators,
  EReportTargetValueTypes,
  IDataValuesFormat,
  IReportConfiguration,
  IReportTarget,
  ITargetLocale,
  OG_META_DATA_KEY,
  OrganizedByColumnType
} from '@opengov/reporting-types';
import {
  CATEGORIZED_BY_COLUMN_ALIAS,
  EReportColumnTypes,
  ORGANIZED_BY_COLUMN_ALIAS
} from '@opengov/reporting-types/src/reportColumns-immer';

import { max, min, sum } from 'lodash';

import { calculateColumnAmountType } from '@opengov/reporting-formatters/formatters';

const currencyRegex = /[$¢€,]+/g;
const nocodeRegex = /-NOCODE/g;
const emptyValuesText = 'Empty Values';

export interface IEmbedFormattedData {
  id: number;
  type: string;
  name: string;
  totals: number;
  data: any;
}

export const ensureString = (value: string | boolean): string => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value);
};

export const getOrgByTotal = ({
  data,
  columns
}: {
  data: IOgqlJobResult['jobResult']['data'] | IOgqlV1JobResult['jobResult']['data'];
  columns: IOgqlJobResult['jobResult']['columns'] | IOgqlV1JobResult['jobResult']['metadata']['columns'];
}) => {
  const ogqlData = data;
  const keyIndexes = getKeyIndexes(columns as IOgqlQueryResult['columns']);

  const orgByTotals = ogqlData.reduce((accu, row) => {
    const orgBy = row[keyIndexes[ORGANIZED_BY_COLUMN_ALIAS]];
    const orgByTotal: number = row[keyIndexes.total];

    return {
      ...accu,
      [orgBy]: orgByTotal
    };
  }, {});

  return sum(Object.keys(orgByTotals).map((orgBy) => parseFloat(orgByTotals[orgBy])));
};

export const getKeyIndexes = (columns: IOgqlColumn[]): { [key: string]: number } =>
  columns.reduce((acc, column, idx) => {
    acc[column.name] = idx;
    return acc;
  }, {});

function toNullableFloat(value: string | number) {
  const parsedValue = parseFloat(`${value}`);
  return isNaN(parsedValue) ? null : parsedValue;
}

export const removeNoCodes = (val: any, empty: string) => {
  // typechecking: there are valid scenarios where (val as string) returns an object that lacks
  // string functions like replace. regex: we need to find/replace all instances, which replace
  // only does if it's regex.
  return typeof val === 'string' ? (val === 'NOCODE' ? empty : val.replace(nocodeRegex, '')) : val;
};

export const ogqlDataToChartFormat = (
  columns: IOgqlQueryResult['columns'] | IOgqlV1QueryResult['metadata']['columns'],
  data: IOgqlQueryResult['data'],
  isPercentOfTotal: boolean
): IDataValuesFormat[] => {
  const keyIndexes = getKeyIndexes(columns as IOgqlQueryResult['columns']);
  const hasPrevChange = keyIndexes.hasOwnProperty('prev_change');

  return data.reduce((acc, r, i) => {
    // boolean comes back as 'false', 'true' or 'null' which will convert to actual boolean values
    // we do not want that for catBy or orgBy they need to stay strings.
    const catByRaw = ensureString(r[keyIndexes[CATEGORIZED_BY_COLUMN_ALIAS]]);
    const orgByRaw = ensureString(r[keyIndexes[ORGANIZED_BY_COLUMN_ALIAS]]);

    const catBy = removeNoCodes(catByRaw, '');
    const orgBy = removeNoCodes(orgByRaw, '');

    const value = r[keyIndexes.value];
    const targetStatus = r[keyIndexes.target_status];
    const prevChange = hasPrevChange ? r[keyIndexes.prev_change] : null;
    const total = r[keyIndexes.total];

    // If the percentageTotal is not a valid number, we just use the original value
    // ... to prevent error on calculation
    // Otherwise use the calculated version

    const percentTotal = toNullableFloat(r[keyIndexes.percent_total])
      ? toNullableFloat(total) === null //If total is null we don't do calculation
        ? r[keyIndexes.percent_total]
        : `${new Decimal(toNullableFloat(value)).dividedBy(toNullableFloat(total)).toNumber()}`
      : r[keyIndexes.percent_total];
    const amount = toNullableFloat(isPercentOfTotal ? percentTotal : value);

    const metaData = {
      prevChange: { [catBy]: prevChange },
      percentTotal: { [catBy]: percentTotal },
      total: { [catBy]: total },
      value: { [catBy]: value },
      targetStatus,
      aggregateTotal: toNullableFloat(total)
    };

    const existing = acc.find((v) => v.name === orgBy);

    if (existing) {
      existing[OG_META_DATA_KEY].prevChange = { ...existing[OG_META_DATA_KEY].prevChange, ...metaData.prevChange };
      existing[OG_META_DATA_KEY].percentTotal = {
        ...existing[OG_META_DATA_KEY].percentTotal,
        ...metaData.percentTotal
      };
      existing[OG_META_DATA_KEY].total = { ...existing[OG_META_DATA_KEY].total, ...metaData.total };
      existing[OG_META_DATA_KEY].value = { ...existing[OG_META_DATA_KEY].value, ...metaData.value };
      existing[catBy] = catBy in existing ? (existing[catBy] as number) + amount : amount;
    } else {
      acc.push({
        id: i,
        name: orgBy,
        [OG_META_DATA_KEY]: metaData,
        ...(amount !== null && { [catBy]: amount })
      });
    }
    return acc;
  }, []);
};

export const defaultAxisFormatter = (n: string | number) => (n ? `${n}` : emptyValuesText);

export const formatTargetConditionValue = (
  value: string,
  localeFormatter: {
    number(params: { value: number }): string;
    percent(parmas: { value: number }): string;
  },
  valueType: EReportTargetValueTypes
): string => {
  const numberValue = parseFloat(value.replace(currencyRegex, ''));
  const isValueTruthy = !isNaN(numberValue) && numberValue !== null;

  if (valueType === EReportTargetValueTypes.percent) {
    return localeFormatter.percent({ value: numberValue });
  }

  if (!isValueTruthy) {
    return null;
  } else if (localeFormatter) {
    return localeFormatter.number({ value: numberValue });
  }

  return numberValue.toString();
};

export const formatTargetConditionOperator = (
  operator: EReportTargetComparisonOperators,
  targetLocaleText: ITargetLocale
): string => {
  return {
    [EReportTargetComparisonOperators.greaterThan]: targetLocaleText.conditions.greaterThan,
    [EReportTargetComparisonOperators.greaterThanOrEqualTo]: targetLocaleText.conditions.greaterThanOrEqualTo,
    [EReportTargetComparisonOperators.lessThan]: targetLocaleText.conditions.lessThan,
    [EReportTargetComparisonOperators.lessThanOrEqualTo]: targetLocaleText.conditions.lessThanOrEqualTo,
    [EReportTargetComparisonOperators.equalTo]: targetLocaleText.conditions.equalTo,
    [EReportTargetComparisonOperators.notEqualTo]: targetLocaleText.conditions.notEqualTo
  }[operator];
};

export const checkTargetCondition = (total: number, value: number) => ({
  [EReportTargetComparisonOperators.greaterThan]: total > value,
  [EReportTargetComparisonOperators.greaterThanOrEqualTo]: total >= value,
  [EReportTargetComparisonOperators.lessThan]: total < value,
  [EReportTargetComparisonOperators.lessThanOrEqualTo]: total <= value,
  [EReportTargetComparisonOperators.equalTo]: total === value,
  [EReportTargetComparisonOperators.notEqualTo]: total !== value
});

export const isFirstCondition = (operator: string, conditionValue: number, values: number[]) => {
  switch (operator) {
    case EReportTargetComparisonOperators.greaterThan:
    case EReportTargetComparisonOperators.greaterThanOrEqualTo:
      // Condition will be first if it has the largest value
      return conditionValue > min(values);
    case EReportTargetComparisonOperators.lessThan:
    case EReportTargetComparisonOperators.lessThanOrEqualTo:
      // Condition will be first if it has the smallest value
      return conditionValue < max(values);
    default:
      return true;
  }
};

export const parseTargetConditionValue = (value) => parseFloat(value.replace(currencyRegex, ''));

export const formatTargetConditionText = (
  target: IReportTarget,
  targetLocaleText: ITargetLocale,
  localeFormatter: {
    number(params: { value: number }): string;
    percent(parmas: { value: number }): string;
  },
  organizeByLabel?: string
): string => {
  if (target && target.conditions) {
    const conditionsList = target.conditions;
    const condition = conditionsList[0];

    if (condition) {
      const operator = formatTargetConditionOperator(condition.operator, targetLocaleText);
      const value = formatTargetConditionValue(condition.value.text, localeFormatter, condition.value.valueType);

      if (organizeByLabel) {
        return `${operator} ${value} ${targetLocaleText.per} ${organizeByLabel}`;
      }

      return `${operator} ${value}`;
    }
  }

  return null;
};

export const determineTargetBadgeProps = (target: IReportTarget, total: number) => {
  if (target && target.conditions && target.elseStatus) {
    const conditionsList = JSON.parse(JSON.stringify(target.conditions));
    const values = conditionsList.map((c) => parseTargetConditionValue(c.value.text));
    let text;
    let variant = 'positive';

    conditionsList.reverse().forEach((c, i, conditions) => {
      const conditionValue = parseTargetConditionValue(c.value.text);

      if (checkTargetCondition(total, conditionValue)[c.operator]) {
        text = c.status.text;

        if (conditions.length > 1) {
          const isFirst = isFirstCondition(c.operator, conditionValue, values);
          variant = isFirst ? 'positive' : 'warning';
        } else {
          variant = 'positive';
          text = c.status.text;
        }
      }
    });

    if (!text) {
      text = target.elseStatus.text;
      variant = 'negative';
    }

    return { text, variant };
  }

  return null;
};

export const buildEmbedInfoFromReportConfiguration = (reportConfiguration: IReportConfiguration, columns) => {
  const { visualizationQuery } = reportConfiguration;
  const { categorizedByColumn, showColumn, organizedBy, chartType } = visualizationQuery;
  const isCategorizedByAggregate = categorizedByColumn.columnType === EReportColumnTypes.aggregate;
  const categorizedByName = isCategorizedByAggregate ? showColumn.displayName : categorizedByColumn.displayName;

  const showColumnAmountType = calculateColumnAmountType(showColumn);
  let yAxisUnits = 'scalar';
  switch (showColumnAmountType) {
    case 'currency':
      yAxisUnits = 'USD';
      break;
    case 'percentage':
      yAxisUnits = 'percent';
      break;
    default:
      break;
  }
  if (visualizationQuery.showColumnAsPercentOfTotal) {
    yAxisUnits = 'percentOfTotal';
  }
  return {
    yAxisUnits,
    xAxisUnits:
      organizedBy.columnType === OrganizedByColumnType.Text ? OrganizedByColumnType.Text : organizedBy.selection,
    formatter: defaultAxisFormatter,
    chartType,
    categorizedByName,
    customColors: reportConfiguration.presentationConfiguration
      ? reportConfiguration.presentationConfiguration.colors
      : createReportConfigurationPresentation().colors
  };
};
